import icTrashRed from "@/assets/icons/ic-trash-red.svg";
import { ConfirmDialog } from "@/components/common/confirm-dialog";
import { Input } from "@/components/ui/input";
import Pagination from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";

import IconHelp from "@/assets/icons/icon-help.svg";
import { SearchInput } from "@/components/common/search-input";
import IconEmail from "@/assets/icons/ic-mail.svg";
import IconPhone2 from "@/assets/icons/ic-phone2.svg";
import IconCompany from "@/assets/icons/icon-company.svg";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate, formatPhoneNumber, STATUS_CONFIG_INQUIRY } from "@/lib/utils";
import { AdminInquiry, InquiryStatus } from "@/types/inquiry";
import { deleteInquiry, getInquiryList, updateInquiryStatus } from "@/services/manage-inquiry";
import { Button } from "@/components/ui/button";
import { usePermissions } from "@/lib/permissions";
import IcNodata from "@/assets/icons/ic-nodata.svg";
import { TruncatedTooltip } from "@/components/ui/truncated-tooltip";

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "전체 상태" },
  { value: "in_progress", label: "처리 중" },
  { value: "completed", label: "답변완료" },
  { value: "deleted", label: "삭제됨" },
];

const PAGE_SIZE = 10;

const ManageInquiryListPage = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canUpdate = can.update("inquiry_management");
  const canDelete = can.delete("inquiry_management");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [inquiries, setInquiries] = useState<AdminInquiry[]>([]);
  const [total, setTotal] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const hasFetchedRef = useRef(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchInquiries = useCallback(async () => {
    if (hasFetchedRef.current) {
      setIsSearching(true);
    }
    try {
      const params: Record<string, string | number> = {
        page: currentPage,
        page_size: PAGE_SIZE,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter !== "all") params.status = statusFilter;

      const res = await getInquiryList(params);
      if (res.data?.success && res.data?.data) {
        setInquiries(res.data.data.items);
        setTotal(res.data.data.total);
      }
    } catch {
      setInquiries([]);
      setTotal(0);
    } finally {
      setIsInitialLoading(false);
      setIsSearching(false);
      hasFetchedRef.current = true;
    }
  }, [currentPage, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const handleClearSearch = () => {
    setSearchQuery("");
    setDebouncedSearch("");
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleDeleteClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDeleteTargetId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteInquiry(deleteTargetId);
      toast({ description: "문의가 삭제되었습니다." });
      fetchInquiries();
    } catch {
      toast({
        description: "문의 삭제에 실패했습니다.",
        variant: "destructive",
      });
    } finally {
      setDeleteTargetId(null);
    }
  };

  const handleStatusUpdate = async (e: React.MouseEvent, id: string, newStatus: InquiryStatus) => {
    e.stopPropagation();
    try {
      await updateInquiryStatus(id, newStatus);
      toast({ description: "상태가 변경되었습니다." });
      fetchInquiries();
    } catch {
      toast({
        description: "상태 변경에 실패했습니다.",
        variant: "destructive",
      });
    }
  };
  return (
    <>
      {isInitialLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div 
        className="bg-white rounded-[16px] flex flex-col gap-6"
        style={{ display: isInitialLoading ? "none" : "flex" }}
      >
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SearchInput
            placeholder="이름, 이메일, 업체명 또는 제목으로 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setDebouncedSearch(searchQuery.trim());
                setCurrentPage(1);
              }
            }}
            onClear={handleClearSearch}
          />
          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[127px]">
              <SelectValue placeholder="전체 상태" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Record Count */}
      <div className="flex">
        <span className="text-sm text-[#B0B0B0]">
          총 {total}개의 문의 중 {inquiries.length}개 표시 중
        </span>
      </div>
      {/* Table */}
      <div
        className={`border border-[rgba(0,0,0,0.09)] rounded overflow-x-auto min-w-0 w-full transition-opacity duration-200 ${isSearching ? "pointer-events-none" : ""}`}
      >
        <Table className="min-w-[1050px]">
          <TableHeader>
            <TableRow className="bg-[#EBEBEB]">
              <TableHead className="w-[224px] max-w-[224px]">문의자명</TableHead>
              <TableHead className="w-[402px] max-w-[402px]">제목</TableHead>
              <TableHead className="w-[149px]">상태 </TableHead>
              <TableHead className="w-[149px]">생성일</TableHead>
              <TableHead className="w-[100px] text-right">관리</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {inquiries.map((inquiry) => {
              const config = STATUS_CONFIG_INQUIRY[inquiry.status];
              return (
                <TableRow
                  key={inquiry.id}
                  className={`cursor-pointer ${inquiry.has_unread ? "font-bold" : ""}`}
                  onClick={() => navigate(`/inquiries/${inquiry.id}`)}
                >
                  <TableCell className="text-[rgba(0,0,0,0.91)] max-w-[224px] p-3 h-[120px]">
                    <div className="flex flex-col gap-[4px]">
                      <div className="flex items-center gap-2">
                        {inquiry.has_unread && (
                          <span className="w-3 h-3 rounded-full bg-[#43946c] shrink-0" />
                        )}
                        <TruncatedTooltip className="text-base leading-6 truncate">
                          {inquiry.contact_info.requester_name}
                        </TruncatedTooltip>
                      </div>
                      <div className="flex gap-1 p-[2px]">
                        <img src={IconEmail} alt="email" className="w-5 h-5 brightness-[4.4]" />
                        <TruncatedTooltip
                          className={`text-xs text-[#000000] truncate ${inquiry.has_unread ? "font-bold" : "font-normal"}`}
                        >
                          {inquiry.contact_info.email}
                        </TruncatedTooltip>
                      </div>

                      <div className="flex gap-1">
                        <img src={IconPhone2} alt="phone" />
                        <span
                          className={`text-xs text-[#000000] ${inquiry.has_unread ? "font-bold" : "font-normal"}`}
                        >
                          {formatPhoneNumber(inquiry.contact_info.phone)}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <img src={IconCompany} alt="company" />
                        <TruncatedTooltip
                          className={`text-xs text-[#000000] ${inquiry.has_unread ? "font-bold" : "font-normal"}`}
                        >
                          {inquiry.contact_info.company_name}
                        </TruncatedTooltip>
                      </div>
                    </div>
                  </TableCell>

                  <TableCell className="text-[rgba(0,0,0,0.91)] max-w-[402px] h-[120px]">
                    <div className="flex flex-col justify-center gap-0.5 h-full w-[402px] ">
                      <TruncatedTooltip
                        className={`text-base text-[#202020] truncate ${inquiry.has_unread ? "font-bold" : "font-medium"}`}
                      >
                        {inquiry.title}
                      </TruncatedTooltip>
                      <TruncatedTooltip
                        className={`text-xs text-[#000000] truncate ${inquiry.has_unread ? "font-bold" : "font-normal"}`}
                      >
                        {inquiry.content}
                      </TruncatedTooltip>
                    </div>
                  </TableCell>
                  <TableCell className="w-[149px] h-[120px]">
                    {canUpdate && inquiry.status !== "deleted" ? (
                      <Select
                        value={inquiry.status}
                        onValueChange={(value) => {
                          const syntheticEvent = {
                            stopPropagation: () => {},
                          } as unknown as React.MouseEvent;
                          handleStatusUpdate(syntheticEvent, inquiry.id, value as InquiryStatus);
                        }}
                      >
                        <SelectTrigger
                          onClick={(e) => e.stopPropagation()}
                          className={`h-7 !text-[14px] w-fit gap-1 rounded border-none  text-xs font-medium [&>img]:hidden ${config.bg} ${config.text} ${config.padding}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in_progress" className="text-[16px]">
                            처리 중
                          </SelectItem>
                          <SelectItem value="completed" className="text-[16px]">
                            답변완료
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div
                        className={`inline-flex items-center gap-1 h-7 rounded text-xs font-medium ${config.bg} ${config.text} ${config.padding}`}
                      >
                        <span>{config.label}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell
                    className={`text-[rgba(0,0,0,0.91)] text-[16px] h-[120px] w-[149px] ${inquiry.has_unread ? "font-bold" : "font-normal"}`}
                  >
                    {formatDate(inquiry.created_at, "YYYY-MM-DD HH:mm")}
                  </TableCell>
                  <TableCell className="h-[120px]">
                    <div className="flex items-center justify-end">
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-[80px] h-[32px] rounded text-[12px] font-bold text-[#000000] bg-[#F7DDCF]"
                          onClick={(e) => handleDeleteClick(e, inquiry.id)}
                        >
                          삭제
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {inquiries.length === 0 && !isInitialLoading && !isSearching && (
              <TableRow>
                <TableCell colSpan={5} className="h-[600px] text-center">
                  <div className="flex flex-col items-center justify-center h-full gap-4">
                    <img src={IcNodata} alt="No data" className="" />
                    <p className="text-[32px] text-[#000000E8]">
                      {debouncedSearch || statusFilter !== "all"
                        ? "검색 결과가 없습니다"
                        : "No data"}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          className="justify-center"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      <ConfirmDialog
        open={deleteTargetId !== null}
        onOpenChange={(open) => !open && setDeleteTargetId(null)}
        iconVariant="neutral"
        icon={<img src={IconHelp} alt="Icon Alert Circle" />}
        title="문의 삭제 확인"
        description="해당 문의를 삭제하시겠습니까?"
        primaryAction={{ label: "네", onClick: handleDeleteConfirm }}
        secondaryAction={{
          label: "아니요",
          onClick: () => setDeleteTargetId(null),
        }}
      />
    </div>
    </>
  );
};

export default ManageInquiryListPage;
